import { useState } from 'react';
import type { Deck, Pilot } from '@edh/shared';
import { deckLabel, display, isRanked, roman } from '@edh/shared';
import { RankedAvatar } from './RankedAvatar';
import { DeckThumb } from './DeckThumb';
import { Sheet } from './Sheet';
import { ColorPips } from '../screens/Decks';
import { useStore } from '../lib/store';

/**
 * Pickers for a seat.
 *
 * Faces rather than dropdowns: at a table you know who is sitting there by
 * looking, and a select box makes you read four names to confirm something you
 * already knew.
 *
 * Nothing is refused. Something another seat holds is labelled with who has it
 * and takes it off them when picked; what this seat already holds clears when
 * picked again. Greying them out looked tidy and made the commonest correction
 * at a table — those two swapped decks — impossible without starting over.
 */

export function PlayerPicker({
  members,
  heldBy,
  selected,
  onPick,
  onClose,
}: {
  members: Pilot[];
  /** id to the name of whoever currently has it. */
  heldBy: Record<string, string>;
  selected: string;
  onPick: (pilotId: string) => void;
  onClose: () => void;
}) {
  const { createPilot } = useStore();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  async function addGuest() {
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      const guest = await createPilot(name);
      onPick(guest.id);
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet title="Who's in this seat?" onClose={onClose}>
      <div className="pick-grid">
        {members.map((p) => {
          const elsewhere = p.id !== selected && p.id in heldBy;
          return (
            <button
              key={p.id}
              className={`pick${p.id === selected ? ' picked' : ''}`}
              onClick={() => {
                onPick(p.id);
                onClose();
              }}
            >
              <RankedAvatar pilot={p} size={52} />
              <span className="pick-name">{p.name}</span>
              {p.id === selected ? (
                <span className="pick-tag">tap to clear</span>
              ) : elsewhere ? (
                <span className="pick-tag">seated</span>
              ) : (
                !p.sub && <span className="pick-tag">guest</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Somebody turned up who is not in the guild. They are rated like anyone
          else, and can claim the record later if they ever join properly. */}
      {adding ? (
        <div style={{ marginTop: 16 }}>
          <label className="field">
            <span>Guest name</span>
            <input
              className="input"
              value={name}
              autoFocus
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void addGuest();
              }}
            />
          </label>
          <div className="btn-row">
            <button className="btn ghost" onClick={() => setAdding(false)}>
              Cancel
            </button>
            <button className="btn primary" disabled={!name.trim() || busy} onClick={() => void addGuest()}>
              {busy ? <span className="spinner" /> : 'Add and seat them'}
            </button>
          </div>
        </div>
      ) : (
        <button className="btn ghost block" style={{ marginTop: 16 }} onClick={() => setAdding(true)}>
          + Add a guest
        </button>
      )}
    </Sheet>
  );
}

export function DeckPicker({
  decks,
  heldBy,
  selected,
  onPick,
  onClose,
}: {
  decks: Deck[];
  /** id to the name of whoever currently has it. */
  heldBy: Record<string, string>;
  selected: string;
  onPick: (deckId: string) => void;
  onClose: () => void;
}) {
  return (
    <Sheet title="Playing what?" onClose={onClose}>
      <div className="list">
        {decks.map((d) => {
          const holder = d.id !== selected ? heldBy[d.id] : undefined;
          return (
            <button
              key={d.id}
              className="list-item"
              style={{
                border: 0,
                width: '100%',
                textAlign: 'left',
                cursor: 'pointer',
                background:
                  d.id === selected ? 'color-mix(in srgb, var(--gold) 16%, var(--panel))' : undefined,
              }}
              onClick={() => {
                onPick(d.id);
                onClose();
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
                <DeckThumb deck={d} size={42} />
                <span style={{ minWidth: 0 }}>
                  <span
                    style={{
                      display: 'block',
                      fontFamily: 'var(--font-display)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {deckLabel(d)}
                  </span>
                  <span className="sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <ColorPips colors={d.colors} />
                    {d.powerLevel && <span className="num">{roman(d.powerLevel)}</span>}
                    {holder && <span className="pick-tag">with {holder}</span>}
                    {d.id === selected && <span className="pick-tag">tap to clear</span>}
                  </span>
                </span>
              </span>
              <span className="num" style={{ fontSize: 14, color: 'var(--muted)' }}>
                {isRanked(d.placementGames) ? display(d.elo) : '—'}
              </span>
            </button>
          );
        })}
      </div>
    </Sheet>
  );
}
